import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { CATEGORIES, STATS, TESTIMONIALS, formatPrice } from "../../data/courses";
import { SectionTitle, StarRating, Badge } from "../../components/ui/UI";
import CourseCard from "../../components/ui/CourseCard";
import styles from "./Home.module.css";
import CategoryIcon from "../../components/ui/CategoryIcon";
import useReveal from "../../hooks/useReveal";

// ── Hero ──────────────────────────────────────────────────────────────────────

const HERO_TYPE = "image"; // "video" | "image" | "none"

function Hero() {
  const navigate = useNavigate();
  return (
    <section className={styles.hero}>
      {HERO_TYPE === "video" && (
        <video
          className={styles.heroBgVideo}
          src="/hero-video.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
      )}
      {HERO_TYPE === "image" && (
        <picture>
          <source media="(max-width: 600px)" srcSet="/hero-image-mobile.png" />
          <img
            className={styles.heroBgVideo}
            src="/hero-image.png"
            alt=""
          />
        </picture>
      )}

      <div className={styles.heroOverlay} />

      <div className={styles.heroBg}>
        <div className={styles.orb1} />
        <div className={styles.orb2} />
      </div>

      <div className={`container ${styles.heroInner}`}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroHeading}>
            <span className={styles.heroGradient}>Small but Satisfying.</span>
          </h1>
          <p className={styles.heroSub}>
            In a world moving at AI speed, you don't need to master everything —
            just enough of everything to stay ahead. Bite-sized PDF courses on AI,
            finance, marketing & more. Pay once, learn forever.
          </p>
          <div className={styles.heroActions}>
            <button
              className={styles.heroCta}
              onClick={() => navigate("/category/all")}
            >
              Browse All Courses →
            </button>
          </div>
          <div className={styles.heroTrust}>
            <div className={styles.heroStars}>
              {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
            </div>
            <span>4.9 avg rating · 10+ students · Instant PDF delivery</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Banner ──────────────────────────────────────────────────────────────
function StatsBanner() {
  const ref = useReveal();
  return (
    <section className={`${styles.stats} reveal`} ref={ref}>
      <div className="container">
        <div className={styles.statsGrid}>
          {STATS.map((s, i) => (
            <div key={i} className={styles.statItem}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Poster / Promo Banner ─────────────────────────────────────────────────────
function PromoPoster() {
  const navigate = useNavigate();
  const ref = useReveal();
  return (
    <section className={`${styles.poster} reveal`} ref={ref}>
      <div className="container">
        <div className={styles.posterInner}>
          <div className={styles.posterBg} />
          <div className={styles.posterContent}>
            <Badge color="gold">✦ EVERY COURSE JUST ₹11</Badge>
            <h2 className={styles.posterHeading}>
              Learn enough to <br />
              <span>lead the conversation</span>
            </h2>
            <p>
              Sharp, concise courses that hand you the core of a topic — fast.
              Walk into any intellectual circle ready to engage, ask better
              questions, and hold your own. All for ₹11 a course.
            </p>
            <button className={styles.posterCta} onClick={() => navigate("/category/all")}>
              Browse Courses →
            </button>
          </div>
          <div className={styles.posterDecor}>
            <div className={styles.posterOrb} />
            <div className={styles.posterIcons}>
              {["💡", "📚", "🧠", "🗣️"].map((e, i) => (
                <div key={i} className={styles.posterIcon}
                  style={{ animationDelay: `${i * 0.5}s` }}
                >{e}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Featured Courses ──────────────────────────────────────────────────────────
function FeaturedCourses() {
  const { courses } = useApp();
  const featured = courses.filter((c) => c.featured);
  const ref = useReveal();
  return (
    <section className={`section-gap ${styles.featuredSection} reveal`} ref={ref}>
      <div className="container">
        <SectionTitle
          eyebrow="Top Picks"
          title="Featured Courses"
          subtitle="Hand-picked PDFs our students love the most. Each one is packed with real, actionable knowledge."
        />
        <div className="grid-3">
          {featured.map((c, i) => (
            <CourseCard key={c.id} course={c} delay={i * 80} />
          ))}
        </div>
        <div className={styles.viewAll}>
          <Link to="/category/all" className={styles.viewAllLink}>
            View All Courses →
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Categories Grid ───────────────────────────────────────────────────────────
function CategoriesGrid() {
  const ref = useReveal();
  return (
    <section className={`section-gap ${styles.catSection} reveal`} ref={ref}>
      <div className="container">
        <SectionTitle
          eyebrow="Browse by topic"
          title="What do you want to master?"
          center
        />
        <div className={styles.catGrid}>
          {CATEGORIES.map((cat, i) => (
            <Link
              key={cat.id}
              to={`/category/${cat.id}`}
              className={styles.catCard}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <span className={styles.catIcon}><CategoryIcon icon={cat.icon} size={32} /></span>
              <strong>{cat.label}</strong>
              <p>{cat.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────
function Testimonials() {
  const ref = useReveal();
  return (
    <section className={`section-gap ${styles.testiSection} reveal`} ref={ref}>
      <div className="container">
        <SectionTitle
          eyebrow="Student Stories"
          title="Real people. Real results."
          subtitle="Don't take our word for it — here's what our students say."
          center
        />
        <div className="grid-2">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className={styles.testiCard} style={{ animationDelay: `${i * 80}ms` }}>
              <StarRating rating={t.rating} />
              <p className={styles.testiText}>"{t.text}"</p>
              <div className={styles.testiAuthor}>
                <div className={styles.avatar}>{t.avatar}</div>
                <div>
                  <strong>{t.name}</strong>
                  <p>{t.role}</p>
                  <small>📚 {t.course}</small>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Us ────────────────────────────────────────────────────────────────────
function WhyUs() {
  const ref = useReveal();
  const items = [
    { icon: "📄", title: "PDF Format", desc: "Read anywhere — phone, tablet, PC. No app, no login required after purchase." },
    { icon: "⚡", title: "Instant Delivery", desc: "PDF lands in your inbox the moment payment is confirmed. Zero wait time." },
    { icon: "🔒", title: "DRM Protected", desc: "Every PDF is password-locked and tied to your email. Can't be shared or pirated." },
    { icon: "💸", title: "Affordable Pricing", desc: "Courses start at just ₹9. Premium knowledge without the premium price tag." },
    { icon: "🎯", title: "Zero Fluff", desc: "Every page earns its place. We cut the filler so you only get what matters." },
    { icon: "♾️", title: "Lifetime Access", desc: "Once you buy, it's yours forever. Revisit whenever you need a refresher." },
  ];
  return (
    <section className={`section-gap ${styles.whySection} reveal`} ref={ref}>
      <div className="container">
        <SectionTitle eyebrow="Why Shortly Life" title="Everything you need. Nothing you don't." center />
        <div className={styles.whyGrid}>
          {items.map((item, i) => (
            <div key={i} className={styles.whyCard} style={{ animationDelay: `${i * 60}ms` }}>
              <div className={styles.whyIcon}>{item.icon}</div>
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <div>
      <Hero />
      <StatsBanner />
      <PromoPoster />
      <FeaturedCourses />
      <CategoriesGrid />
      <Testimonials />
      <WhyUs />
    </div>
  );
}